import { isEqual, isArray, isObject, transform, isObjectLike } from 'lodash';
import { inTestMode } from '../constants/test.constants';
import { isDefined } from './array.helpers';
import { getKeys, isNonArrayObject, omit } from './object.helpers';
import { DeepPartial } from '@shared/models/type-helpers.model';

/**
 * Wrap the return value of a function to log the return value
 * @param values
 * @returns
 */
export const logReturn = <T>(
  ...values: [returnValue: T, ...logValues: unknown[]]
): T => {
  const [returnValue, ...otherValues] = values;
  console.info(...otherValues, returnValue);
  return returnValue;
};

/**
 * Log the time it takes to execute a function
 * @param fn
 * @param label
 * @param enabled Whether to log the time or not
 * @returns
 */
export const logTime = <T>(
  fn: () => T,
  label: string = fn.name,
  enabled = false,
): T => {
  if (!enabled) {
    return fn();
  }
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  console.info(`${label} took ${end - start}ms`);
  return result;
};

const timeRecord: Record<string, number> = {};

/**
 * Accumulate elapsed time under a label and log total. Use when you cannot wrap
 * a call in logTotalTime (e.g. React hooks must be called at top level).
 * @param label - Label to track time by (same as in logTotalTime)
 * @param elapsedMs - Elapsed time in milliseconds
 * @param enabled - Whether to record and log
 */
export const accumulateTotalTime = (
  label: string,
  elapsedMs: number,
  enabled = false,
): void => {
  if (!enabled) return;
  const total = (timeRecord[label] ?? 0) + elapsedMs;
  timeRecord[label] = total;
  console.info(`${label} has been executed ${timeRecord[label]}ms`);
};

/**
 * Log the total time a funtion has taken to execute
 * @param fn
 * @param label label to keep track of the time by
 * @param enabled Whether to log the time or not
 * @returns
 */
export const logTotalTime = <T>(
  fn: () => T,
  label: string = fn.name,
  enabled = false,
): T => {
  if (!enabled) {
    return fn();
  }
  const start = performance.now();
  const result = fn();
  const end = performance.now();
  const total = (timeRecord[label] ?? 0) + end - start;
  timeRecord[label] = total;
  console.info(`${label} has been executed ${timeRecord[label]}ms`);
  return result;
};

export const createOptionalConsole = <
  K extends 'log' | 'count' | 'error' | 'warn' | 'info' = 'log',
>(
  enabled: boolean,
  method: K = 'log' as K,
  // eslint-disable-next-line no-console
): Console[K] => (enabled ? console[method] : () => {});

/**
 * Console `info` / `warn` / `error` that are no-ops in Vitest ({@link inTestMode}).
 */
export const logOutsideTest = {
  info: (...args: Parameters<typeof console.info>): void => {
    createOptionalConsole(!inTestMode(), 'info')(...args);
  },
  warn: (...args: Parameters<typeof console.warn>): void => {
    createOptionalConsole(!inTestMode(), 'warn')(...args);
  },
  error: (...args: Parameters<typeof console.error>): void => {
    createOptionalConsole(!inTestMode(), 'error')(...args);
  },
} as const;

type GetDifferenceOptions = {
  /**
   * Keys to ignore from the comparison.
   */
  ignoreKeys?: string[];

  /**
   * Keys to include when a change is detected even though these keys have not changed.
   * Helpful if you wanna get the name/id of the element that changed.
   */
  includeKeys?: string[];

  /**
   * Whether to show missing keys as key: undefined in result
   */
  showMissingKeys?: boolean;
};
/**
 * Get the difference between two objects
 * @param origObj
 * @param newObj
 * @returns
 */
export function getDifference<T extends object | unknown[]>(
  newObj: T,
  origObj: unknown,
  {
    ignoreKeys = [],
    includeKeys,
    showMissingKeys = false,
  }: GetDifferenceOptions = {},
): DeepPartial<T> {
  if (!isDefined(origObj) || !isDefined(newObj)) {
    return newObj;
  }
  origObj = omitDeep(origObj, ...ignoreKeys);
  newObj = omitDeep(newObj, ...ignoreKeys);

  const removeKeys = <T>(obj: T) =>
    isNonArrayObject(obj) && ignoreKeys?.length
      ? omit(obj, ...(ignoreKeys as (keyof typeof obj)[]))
      : obj;

  function changes(newObj: any, origObj: any): any {
    // Add all missing keys from the original object
    if (isObject(newObj) && isObject(origObj) && showMissingKeys) {
      getKeys(origObj).forEach((key) => {
        if (!(key in newObj)) {
          newObj = { ...newObj, [key]: undefined };
        }
      });
    }

    let arrayIndexCounter = 0;

    return transform(newObj, function (result, value, key) {
      // Remove keys that should not be compared (like floats or properties you knopw will change)
      const originalValue = removeKeys(origObj[key]);
      value = removeKeys(value);

      if (!isEqual(value, originalValue)) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;

        result[resultKey] =
          isObject(value) && isObject(originalValue)
            ? changes(value, originalValue)
            : value;

        // Add extra keys like name/id to the result if they exist in the new object
        if (isNonArrayObject(value) && includeKeys?.length) {
          includeKeys.forEach((k) => {
            const extraValue =
              k in value ? value[k as keyof typeof value] : undefined;
            if (extraValue !== undefined) {
              result[resultKey] = {
                ...result[resultKey],
                [k]: extraValue,
              };
            }
          });
        }
      }
    });
  }

  return changes(newObj, origObj);
}

/**
 * Get the difference between two objects. Will also include _[key] with the original value on the primitives changed
 * @param newObj
 * @param origObj
 * @param settings
 * @returns
 */
export const compareObjects = <T extends object | unknown[]>(
  newObj: T,
  origObj: T,
  settings: Pick<GetDifferenceOptions, 'ignoreKeys' | 'includeKeys'> = {},
): any => {
  if (!isDefined(origObj) || !isDefined(newObj)) {
    return { new: newObj, orig: origObj };
  }

  const { ignoreKeys = [] } = settings;
  const cleanedOrigObj = omitDeep(origObj, ...ignoreKeys);
  const cleanedNewObj = omitDeep(newObj, ...ignoreKeys);

  function addOriginalValues(newObj: any, origObj: any, result: any = {}): any {
    for (const key in newObj) {
      if (Object.prototype.hasOwnProperty.call(newObj, key)) {
        const newValue = newObj[key];
        const origValue = origObj?.[key];

        if (isObject(newValue) && isObject(origValue) && !isArray(newValue)) {
          // Recursively handle nested objects
          result[key] = addOriginalValues(
            newValue,
            origValue,
            result[key] || {},
          );
        } else if (!isEqual(newValue, origValue)) {
          // For primitives and arrays, add both new value and original with underscore
          result[key] = newValue;
          result[`_${key}`] = origValue;
        }
      }
    }
    return result;
  }

  const changes = getDifference(cleanedNewObj, cleanedOrigObj, {
    ...settings,
    showMissingKeys: true,
  });
  return addOriginalValues(changes, cleanedOrigObj, {}) as DeepPartial<T>;
};

/**
 * Omit keys from an object recursively. Only use for debugging since it's quite ugly & slow.
 * @param obj
 * @param keys
 * @returns
 */
export const omitDeep = <T>(obj: T, ...keys: string[]): T => {
  if (keys.length && (Array.isArray(obj) || isObjectLike(obj))) {
    return JSON.parse(
      JSON.stringify(obj, (key, value) => {
        if (keys.includes(key)) {
          return undefined;
        }
        return value as unknown;
      }),
    ) as T;
  }
  return obj;
};
